import { type BarcodeSymbology, symbologyFromDetectedFormat, toCanonicalGtin } from '@gramas/shared'
import type { BarcodeDetector } from 'barcode-detector/ponyfill'

/**
 * Barcode decoding.
 *
 * Safari still ships no BarcodeDetector outside an off-by-default flag, so the
 * WebAssembly ponyfill is the only path that behaves identically on both
 * platforms. Where Chrome for Android does expose a native detector covering
 * every format we need, we use it — but the check is on the supported format
 * list, not on the class existing, because desktop Chrome exposes the class and
 * then detects nothing.
 */

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'qr_code'] as const

let detectorPromise: Promise<BarcodeDetector> | null = null
let wasmBasePath = '/vendor/zxing/'

export function configureBarcodeWasm(basePath = '/vendor/zxing/'): void {
  wasmBasePath = basePath
}

async function nativeCovers(): Promise<boolean> {
  const Native = (globalThis as { BarcodeDetector?: typeof BarcodeDetector }).BarcodeDetector
  if (!Native?.getSupportedFormats) return false
  try {
    const supported = await Native.getSupportedFormats()
    return FORMATS.every((f) => (supported as readonly string[]).includes(f))
  } catch {
    return false
  }
}

async function buildDetector(): Promise<BarcodeDetector> {
  const Native = (globalThis as { BarcodeDetector?: typeof BarcodeDetector }).BarcodeDetector
  if (await nativeCovers()) {
    return new Native!({ formats: [...FORMATS] })
  }
  // Only pulled in where the platform has no usable detector of its own,
  // which is every iOS browser.
  const module = await import('barcode-detector/ponyfill')
  module.prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? `${wasmBasePath}${path}` : `${prefix}${path}`,
    },
  })
  return new module.BarcodeDetector({ formats: [...FORMATS] })
}

/**
 * The decoder, built once.
 *
 * A *failure* is deliberately not remembered. The megabyte of WebAssembly is
 * fetched on first use rather than precached, so the first attempt is the one
 * most likely to lose to a tunnel or a cold cache — and caching that rejected
 * promise left the frame loop asking a broken detector 8 times a second for the
 * rest of the session, silently, while the screen said "point at a barcode".
 */
export async function getDetector(): Promise<BarcodeDetector> {
  detectorPromise ??= buildDetector().catch((err) => {
    detectorPromise = null
    throw err
  })
  return detectorPromise
}

export interface ScanHit {
  /** Canonical 13-digit GTIN, or the raw payload for QR codes. */
  value: string
  raw: string
  symbology: BarcodeSymbology
  isGtin: boolean
}

/**
 * Accepts a decode only once the same value has won a majority of the window.
 *
 * A single frame is not evidence: curved cans and glossy film produce misreads
 * that still satisfy the GS1 check digit. So a linear code has to come back
 * from three of the last five frames; QR needs two, because it carries its own
 * error correction and a decode is already self-checking.
 *
 * The agreeing frames need not be consecutive, and that is the point. There is
 * no focus control on iOS, so a packet held at arm's length decodes
 * intermittently rather than steadily — and the old rule wanted two agreeing
 * frames back to back, which a feed reading the code every other frame never
 * produces. Plainly good enough, and nothing happened: pointing at a code did
 * nothing at all. Dropping adjacency costs no safety the vote does not already
 * buy, since three frames landing on the same wrong-but-valid GTIN is exactly
 * as unlikely whether or not they are neighbours.
 */
export class ScanVoter {
  private window: Array<string | null> = []
  private cooldownUntil = 0
  private static readonly WINDOW = 5
  private static readonly GTIN_VOTES = 3
  private static readonly QR_VOTES = 2
  private static readonly COOLDOWN_MS = 3000

  reset(): void {
    this.window = []
    this.cooldownUntil = 0
  }

  /** Feed one frame's best candidate (or null when nothing decoded). */
  offer(hit: ScanHit | null, nowMs: number = performance.now()): ScanHit | null {
    if (nowMs < this.cooldownUntil) return null

    this.window.push(hit ? hit.value : null)
    if (this.window.length > ScanVoter.WINDOW) this.window.shift()
    if (!hit) return null

    const votes = this.window.filter((v) => v === hit.value).length
    if (votes < (hit.isGtin ? ScanVoter.GTIN_VOTES : ScanVoter.QR_VOTES)) return null

    this.accept(nowMs)
    return hit
  }

  private accept(nowMs: number): void {
    this.window = []
    this.cooldownUntil = nowMs + ScanVoter.COOLDOWN_MS
  }
}

/** Normalise a raw detection, dropping linear codes that fail their checksum. */
export function toScanHit(raw: string, format: string): ScanHit | null {
  const symbology = symbologyFromDetectedFormat(format)
  if (!symbology) return null

  if (symbology === 'qr') {
    return { value: raw, raw, symbology, isGtin: false }
  }

  const canonical = toCanonicalGtin(raw, symbology)
  if (!canonical) return null
  return { value: canonical, raw, symbology, isGtin: true }
}
